/**
 * Utilitários puros do painel inicial (E13.F4). // ux-neutrality:allow doc-comment
 *
 * Sem chamadas a rede, sem hooks — funções deterministas que serializam o
 * que `HomePage` precisa para renderizar saudação, KPIs, casos recentes
 * e prazos próximos. Mantidas separadas para terem cobertura focada.
 */
import type { CaseRecordData } from './cases';

export type GreetingSlot = 'morning' | 'afternoon' | 'evening';

const DAY_MS = 24 * 60 * 60 * 1000;
const NEAR_DAYS = 7;

export function pickGreetingSlot(now: Date): GreetingSlot {
  const hour = now.getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * Extrai um primeiro nome amigável do e-mail. Convenções:
 *   alice@example.com    → "Alice"
 *   ana.maria@x.com      → "Ana"
 *   perito_grafo@y.com   → "Perito"
 *   (string vazia)       → "" (consumer decide fallback)
 */
export function deriveFirstName(email: string | null | undefined): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  const firstChunk = local.split(/[._-]/)[0] ?? '';
  if (firstChunk.length === 0) return '';
  return firstChunk.charAt(0).toUpperCase() + firstChunk.slice(1).toLowerCase();
}

export interface PanelKpis {
  casosNoMes: number;
  ativos: number;
  aguardandoLaudo: number;
}

export function computePanelKpis(cases: readonly CaseRecordData[], now: Date): PanelKpis {
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  return cases.reduce<PanelKpis>(
    (acc, c) => {
      const created = new Date(c.created_at);
      if (created.getUTCFullYear() === year && created.getUTCMonth() === month) {
        acc.casosNoMes += 1;
      }
      // Saudação diz "casos em análise" — contar apenas `analysing`. Rascunhos
      // (status=draft) NÃO contam aqui (F-E13.F4-02). Para "trabalho ativo
      // geral" o painel também mostra a section Continuar trabalhando.
      if (c.status === 'analysing') acc.ativos += 1;
      if (c.status === 'conflict') acc.aguardandoLaudo += 1;
      return acc;
    },
    { casosNoMes: 0, ativos: 0, aguardandoLaudo: 0 },
  );
}

export function pickRecentCases(
  cases: readonly CaseRecordData[],
  limit: number = 3,
): CaseRecordData[] {
  return [...cases]
    .filter((c) => c.status !== 'done')
    .sort((a, b) => (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at))
    .slice(0, limit);
}

export interface DeadlineEntry {
  case: CaseRecordData;
  daysAway: number;
}

export function pickUpcomingDeadlines(
  cases: readonly CaseRecordData[],
  now: Date,
): DeadlineEntry[] {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return cases
    .filter((c) => c.deadline !== null && c.deadline !== undefined && c.status !== 'done')
    .map((c) => {
      const deadlineDate = new Date(`${c.deadline}T00:00:00Z`).getTime();
      const days = Math.floor((deadlineDate - today.getTime()) / DAY_MS);
      return { case: c, daysAway: days };
    })
    .filter((entry) => entry.daysAway <= NEAR_DAYS)
    .sort((a, b) => a.daysAway - b.daysAway);
}

export function targetPathForCase(c: CaseRecordData): string {
  return c.status === 'draft' ? `/casos/${c.id}/wizard` : `/casos/${c.id}/workspace`;
}
